<template>
  <div class="tcm-study-page" :class="{ 'is-native': isNativeShell }">
    <van-loading v-if="loading" class="page-loading" color="#2e8b7a">加载中...</van-loading>

    <template v-else>
      <div v-if="pageError" class="error-panel">
        <van-empty image="network" :description="pageError" />
        <van-button block type="primary" color="#2e8b7a" @click="loadAll">重新加载</van-button>
      </div>

      <div v-else-if="!hasStudyAccess" class="empty-panel">
        <van-empty image="search" description="当前账号暂未开通研究模块" />
      </div>

      <main v-else class="content">
        <section class="page-heading">
          <span>{{ selectedDate }}</span>
        </section>

        <section class="daily-entry-card" @click="scrollToTasks">
          <div class="daily-entry-main">
            <span>每日照护任务</span>
            <strong>{{ taskProgressText }}</strong>
            <em>{{ currentBabyName }} · {{ selectedDateIsToday ? '今日照护' : '补录照护' }}</em>
          </div>
          <div class="daily-entry-action">
            <van-icon name="todo-list-o" />
            <b>{{ selectedDateIsToday ? '去完成' : '去补录' }}</b>
          </div>
        </section>

        <section class="profile-strip">
          <div>
            <span>{{ currentBabyName }}</span>
            <em>{{ babyProfile.corrected_age_text || '矫正月龄待完善' }}</em>
          </div>
          <button v-if="showBabySwitcher" class="baby-switcher" type="button" @click="showBabyPicker = true">
            <van-icon name="exchange" />
            切换宝宝
            <van-icon name="arrow-down" />
          </button>
        </section>

        <section class="calendar-section">
          <h2>护理日历</h2>
          <div class="date-strip">
            <button
              v-for="item in calendarDays"
              :key="item.date"
              class="date-item"
              :class="[
                { 'is-active': item.date === selectedDate },
                `is-${item.completionStatus}`
              ]"
              type="button"
              @click="selectDate(item.date)"
            >
              <span>{{ item.week }}</span>
              <strong>{{ item.isToday ? '今日' : item.day }}</strong>
              <em>{{ item.isToday ? item.day : '' }}</em>
              <i>{{ item.completionText }}</i>
            </button>
          </div>
          <p class="calendar-tip">{{ calendarTipText }}</p>
        </section>

        <section ref="taskSection" class="timeline-section">
          <div class="section-title-row">
            <h2>{{ selectedDateIsToday ? '今日任务' : '当日任务' }}</h2>
            <span>{{ taskProgressText }}</span>
          </div>

          <div class="task-timeline">
            <div
              v-for="(task, taskIndex) in displayDailyTasks"
              :key="task.key"
              class="timeline-row"
              :class="{ 'is-last': taskIndex === displayDailyTasks.length - 1 }"
            >
              <div class="time-label">{{ task.time_label }}</div>
              <div class="line-cell"><span></span></div>
              <div
                class="task-card"
                :class="[`is-${task.type}`, `status-${task.status}`, { 'is-clickable': isFeedingTask(task) || isFoodTask(task) || task.type === 'massage' }]"
                role="button"
                tabindex="0"
                @click="onTaskCardClick(task)"
                @keyup.enter="onTaskCardClick(task)"
              >
                <div class="task-main">
                  <div>
                    <h3>{{ task.title }}</h3>
                    <p v-if="taskSubtitle(task)">{{ taskSubtitle(task) }}</p>
                  </div>
                  <van-tag :color="statusColor(task)" plain>{{ task.status_text }}</van-tag>
                </div>

                <div v-if="isFeedingTask(task)" class="feeding-summary">
                  <span>{{ feedingSummaryText }}</span>
                  <em>{{ task.action_text || '编辑' }}</em>
                </div>

                <div v-if="isFoodTask(task)" class="food-recommend-title">
                  <span></span>
                  <b>辅食推荐</b>
                  <span></span>
                </div>
                <div v-if="isFoodTask(task)" class="food-recommend-card">
                  <div
                    v-for="food in foodRecommendations(task)"
                    :key="food.code || food.name"
                    class="food-recommend-item"
                    role="button"
                    tabindex="0"
                    @click.stop="openFoodRecipe(food, task)"
                    @keyup.enter.stop="openFoodRecipe(food, task)"
                  >
                    <div class="food-image">
                      <img
                        v-if="food.image && !foodImageFailed(food)"
                        :src="food.image"
                        :alt="food.name"
                        referrerpolicy="no-referrer"
                        @error.stop="markFoodImageFailed(food)"
                      >
                      <span v-else>{{ food.name.slice(0, 1) }}</span>
                    </div>
                    <div class="food-info">
                      <b>{{ food.name }}</b>
                      <div v-if="food.tags.length" class="food-recommend-tags">
                        <van-tag
                          v-for="tag in food.tags"
                          :key="food.code + '-' + tag"
                          color="#FFF7E8"
                          text-color="#B46B12"
                        >{{ tag }}</van-tag>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="isFoodTask(task) && foodRecordSummary(task)" class="food-record-summary">
                  {{ foodRecordSummary(task) }}
                </div>

                <div v-if="isFoodTask(task)" class="food-add-row">
                  <van-tag v-if="isTaskDone(task)" color="#EAF7F2" text-color="#2E8B7A">已完成</van-tag>
                  <button type="button" @click.stop="openNativeFood(task)">
                    {{ isTaskDone(task) ? '继续添加' : '添加' }}
                  </button>
                </div>

                <div class="task-actions">
                  <button
                    v-if="task.type === 'massage' && task.status === 'not_started'"
                    class="tutorial-card"
                    :class="{ 'is-locked': task.locked }"
                    type="button"
                    @click.stop="openMassageCheck(task)"
                  >
                    <span>
                      <b>{{ massageTaskGuideTitle(task) }}</b>
                      <small>{{ task.locked ? '完成培训后开放' : '温阳、健脾、保健' }}</small>
                    </span>
                    <em>{{ task.locked ? '已锁定' : '去查看' }}</em>
                    <van-icon v-if="task.locked" name="lock" class="lock-icon" />
                  </button>
                  <button v-if="task.type === 'massage' && canContinueMassage(task)" class="tutorial-card" type="button" @click.stop="continueMassage(task)">
                    <span>
                      <b>{{ massageTaskGuideTitle(task) }}</b>
                      <small>继续本次{{ task.title }}流程</small>
                    </span>
                    <em>{{ task.status === 'completed_pending_feedback' ? '去反馈' : '继续操作' }}</em>
                  </button>
                  <button v-if="task.type === 'massage' && task.status === 'tutorial_ready' && massageTutorialVisited" type="button" class="primary" @click.stop="openCompleteDialog">
                    完成{{ task.title }}
                  </button>
                  <span v-if="task.type === 'massage' && task.status === 'completed'" class="done-text">
                    操作 {{ task.duration_minutes || taskRecord.duration_minutes || '-' }} 分钟
                  </span>
                  <span v-if="task.type === 'massage' && task.status === 'aborted'" class="aborted-text">
                    {{ task.abnormal_desc || '存在异常，已终止推拿' }}
                  </span>
                  <button v-if="task.type === 'massage' && showMassagePersistentEntry(task)" class="tutorial-card is-persistent" type="button" @click.stop="isMassageCompleted(task) ? startNewMassage(task) : continueMassage(task)">
                    <span>
                      <b>{{ massagePersistentTitle(task) }}</b>
                      <small>{{ massagePersistentDesc(task) }}</small>
                    </span>
                    <em>{{ massagePersistentAction(task) }}</em>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </template>

    <van-action-sheet
      v-model="showBabyPicker"
      title="切换宝宝"
      :actions="babyActions"
      cancel-text="取消"
      :close-on-click-action="false"
      @select="onBabySelect"
    />

    <van-dialog
      v-model="showMassageCheck"
      title="推拿前检查"
      show-cancel-button
      confirm-button-text="提交"
      :before-close="beforeMassageCheckClose"
    >
      <div class="check-dialog">
        <van-cell title="是否哭闹">
          <template #right-icon>
            <van-switch v-model="massageCheck.crying" size="22" active-color="#f2b51d" />
          </template>
        </van-cell>
        <van-cell title="昨晚睡眠是否正常">
          <template #right-icon>
            <van-switch v-model="massageCheck.sleepNormal" size="22" active-color="#2e8b7a" />
          </template>
        </van-cell>
        <van-cell title="皮肤是否正常">
          <template #right-icon>
            <van-switch v-model="massageCheck.skinNormal" size="22" active-color="#2e8b7a" />
          </template>
        </van-cell>
        <van-field
          v-if="hasMassageAbnormal"
          v-model="massageCheck.abnormalDesc"
          type="textarea"
          rows="2"
          autosize
          label="异常情况"
          placeholder="请填写异常情况"
        />
      </div>
    </van-dialog>

    <van-dialog
      v-model="showCompleteDialog"
      title="完成推拿"
      show-cancel-button
      confirm-button-text="提交"
      :before-close="beforeCompleteClose"
    >
      <div class="complete-dialog">
        <van-field
          v-model.number="completeForm.durationMinutes"
          type="number"
          label="操作时长"
          placeholder="请输入分钟数"
        />
      </div>
    </van-dialog>

    <van-dialog
      v-model="showPreviousMassageDialog"
      title="补录上次推拿记录"
      show-cancel-button
      cancel-button-text="暂不补录"
      confirm-button-text="保存并继续"
      :before-close="beforePreviousMassageClose"
    >
      <div class="previous-massage-dialog">
        <van-field :value="previousMassageForm.operatedDate" label="推拿日期" placeholder="请选择日期" readonly is-link @click="showPreviousDatePicker = true" />
        <van-field :value="previousMassageForm.operatedTime" label="推拿时间" placeholder="请选择时间" readonly is-link @click="showPreviousTimePicker = true" />
        <van-field v-model.number="previousMassageForm.durationMinutes" type="number" label="操作时长" placeholder="请输入分钟数" />
      </div>
    </van-dialog>

    <van-popup v-model="showPreviousDatePicker" position="bottom">
      <van-datetime-picker v-model="previousDateValue" type="date" :min-date="previousDateMin" :max-date="previousDateMax" @confirm="onPreviousDateConfirm" @cancel="showPreviousDatePicker = false" />
    </van-popup>

    <van-popup v-model="showPreviousTimePicker" position="bottom">
      <van-datetime-picker v-model="previousTimeValue" type="time" @confirm="onPreviousTimeConfirm" @cancel="showPreviousTimePicker = false" />
    </van-popup>
  </div>
</template>

<script>
import {
    fetchDailyFeedingStatus,
    fetchTcmStudyHome,
    resolveTcmStudyToken,
    submitMassageCheck,
    submitMassageComplete,
    submitPreviousMassageRecord,
    submitTaskComplete
} from '@/api/tcmStudy';
import { buildPublicApiUrl } from '@/utils/config';

const MASSAGE_TUTORIAL_URL = 'https://yboss.yubinglabs.com/discuz/news/2026-04-21/c5aHLWJ6Pnj.html';
const MASSAGE_PLAN_2_TUTORIAL_URL = 'https://yboss.yubinglabs.com/discuz/news/2026-07-03/36cEcYvh1E6.html';
const RECIPE_IMAGE_PROXY_HOSTS = ['stlib.qbb6.com'];
const MASSAGE_PLAN_TEXT = {
    1: '小儿推拿方案1',
    2: '小儿推拿方案2'
};

function pad (value) {
    const text = String(value);
    return text.length >= 2 ? text : `0${text}`;
}

function formatDate (date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function normalizeFoodImageUrl (url) {
    const imageUrl = String(url || '').trim();
    if (!imageUrl) {
        return '';
    }
    try {
        const parsed = new URL(imageUrl, window.location.origin);
        if (RECIPE_IMAGE_PROXY_HOSTS.indexOf(parsed.hostname) !== -1) {
            const encodedUrl = btoa(unescape(encodeURIComponent(parsed.href)))
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=+$/, '');
            return buildPublicApiUrl(`/common/home_banner/${encodedUrl}?recipe_image=1`);
        }
        return parsed.href;
    } catch (error) {
        return imageUrl;
    }
}

function addDays (date, days) {
    const next = new Date(date.getTime());
    next.setDate(next.getDate() + days);
    return next;
}

function createDefaultHome () {
    return {
        is_enrolled: false,
        has_study_access: false,
        can_submit: false,
        cannot_submit_reason: '',
        subject: null,
        today: {},
        baby_profile: {},
        daily_tasks: [],
        task_record: null
    };
}

function firstValue (sources, keys) {
    for (let i = 0; i < sources.length; i += 1) {
        const source = sources[i];
        if (!source || typeof source !== 'object') {
            continue;
        }
        for (let j = 0; j < keys.length; j += 1) {
            const value = source[keys[j]];
            if (value !== undefined && value !== null && value !== '') {
                return value;
            }
        }
    }
    return '';
}

function resolveMassagePlanNo (home) {
    const subject = home && home.subject && typeof home.subject === 'object' ? home.subject : {};
    const massageEntry = home && home.massage_entry && typeof home.massage_entry === 'object' ? home.massage_entry : {};
    const taskRecord = home && home.task_record && typeof home.task_record === 'object' ? home.task_record : {};
    const raw = String(firstValue([massageEntry, subject, taskRecord], [
        'massage_plan',
        'massage_plan_no',
        'plan_no',
        'plan_code',
        'scheme_no',
        'scheme_code',
        'group_code',
        'group_type',
        'random_group',
        'allocation_group',
        'assigned_group',
        'intervention_type'
    ])).trim().toLowerCase();

    if (!raw) {
        return 1;
    }
    if (raw === '2' || raw === 'b' || raw === 'group_b' || raw === 'b_group' || raw.indexOf('方案2') !== -1 || raw.indexOf('方案二') !== -1 || raw.indexOf('plan2') !== -1 || raw.indexOf('plan_2') !== -1 || raw.indexOf('scheme2') !== -1 || raw.indexOf('scheme_2') !== -1) {
        return 2;
    }
    return 1;
}

function massagePlanTitle (home) {
    return MASSAGE_PLAN_TEXT[resolveMassagePlanNo(home)] || MASSAGE_PLAN_TEXT[1];
}

function massagePlanTutorialUrl (planNo, fallbackUrl = '') {
    if (Number(planNo) === 2) {
        return MASSAGE_PLAN_2_TUTORIAL_URL;
    }
    return fallbackUrl || MASSAGE_TUTORIAL_URL;
}

function normalizeMassageTaskPlan (task, home) {
    if (!task || task.type !== 'massage') {
        return task;
    }
    const planNo = resolveMassagePlanNo(home);
    const planTitle = MASSAGE_PLAN_TEXT[planNo] || MASSAGE_PLAN_TEXT[1];
    return {
        ...task,
        title: planTitle,
        massage_plan_no: planNo,
        massage_plan_title: planTitle,
        tutorial_url: massagePlanTutorialUrl(planNo, task.tutorial_url)
    };
}

function normalizeMassageTasksPlan (tasks, home) {
    return tasks.map(task => normalizeMassageTaskPlan(task, home));
}

function createEveningMassageTask (tasks, home) {
    const massageTask = tasks.find(task => task && task.type === 'massage');
    if (!massageTask) {
        const massageEntry = home && home.massage_entry && typeof home.massage_entry === 'object' ? home.massage_entry : {};
        const status = 'not_started';
        const statusTextMap = {
            not_started: '待查看',
            prepared: '进行中',
            timing: '进行中',
            paused: '进行中',
            tutorial_ready: '待完成',
            completed_pending_feedback: '待反馈',
            completed: '已完成',
            completed_normal: '已完成',
            completed_abnormal: '已完成',
            aborted: '已终止',
            unsuitable: '不适合'
        };
        return {
            key: 'evening_massage',
            task_key: 'evening_massage',
            time_label: '1小时后',
            type: 'massage',
            title: massagePlanTitle(home),
            summary: '温阳、健脾、保健',
            status,
            status_text: statusTextMap[status] || '待查看',
            entry_status: massageEntry.entry_status || 'available',
            locked: massageEntry.entry_status === 'locked' || massageEntry.locked === true,
            lock_reason: massageEntry.lock_reason || '',
            tutorial_url: massagePlanTutorialUrl(resolveMassagePlanNo(home), massageEntry.tutorial_url)
        };
    }
    return {
        ...massageTask,
        key: 'evening_massage',
        task_key: 'evening_massage',
        time_label: '1小时后',
        status: 'not_started',
        status_text: '待查看',
        duration_minutes: null,
        duration_seconds: null,
        abnormal_desc: '',
        discomfort_desc: '',
        feedback_status: 'pending',
        post_operation_status: '',
        post_operation_status_text: '',
        unsuitable_reason_types: [],
        unsuitable_reason_other_desc: '',
        unsuitable_reason_desc: ''
    };
}

function ensureEveningMassageTask (tasks, home) {
    if (!Array.isArray(tasks) || !tasks.length) {
        return [];
    }
    const eveningIndex = tasks.findIndex(task => task && (
        task.key === 'evening_milk'
        || task.time_label === '傍晚'
        || task.time_label === '傍晚后'
    ));
    if (tasks.some((task, index) => task && task.type === 'massage' && (
        task.key === 'evening_massage'
        || task.slot === 'evening_massage'
        || (eveningIndex !== -1 && index > eveningIndex)
    ))) {
        return tasks;
    }
    const eveningMassageTask = createEveningMassageTask(tasks, home);
    const insertIndex = eveningIndex === -1 ? tasks.length : eveningIndex + 1;
    return [
        ...tasks.slice(0, insertIndex),
        eveningMassageTask,
        ...tasks.slice(insertIndex)
    ];
}

function normalizeHome (home) {
    const dailyTasks = Array.isArray(home && home.daily_tasks) ? home.daily_tasks : [];
    const plannedTasks = normalizeMassageTasksPlan(ensureEveningMassageTask(dailyTasks, home), home);
    return {
        ...createDefaultHome(),
        ...(home && typeof home === 'object' ? home : {}),
        today: home && home.today && typeof home.today === 'object' ? home.today : {},
        subject: home && home.subject && typeof home.subject === 'object' ? home.subject : null,
        baby_profile: home && home.baby_profile && typeof home.baby_profile === 'object' ? home.baby_profile : {},
        daily_tasks: plannedTasks,
        task_record: home && home.task_record && typeof home.task_record === 'object' ? home.task_record : null
    };
}

function tutorialVisitKey (babyId, date) {
    return `tcm_massage_tutorial_visited_${babyId || '0'}_${date || ''}`;
}

function normalizeNumber (value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
}

export default {
    name: 'TcmStudyHome',
    data () {
        const today = formatDate(new Date());
        return {
            token: '',
            loading: false,
            pageError: '',
            isNativeShell: false,
            mockEnabled: false,
            currentBabyId: '',
            babies: [],
            selectedDate: today,
            showBabyPicker: false,
            showMassageCheck: false,
            showCompleteDialog: false,
            showPreviousMassageDialog: false,
            showPreviousDatePicker: false,
            showPreviousTimePicker: false,
            submittingPreviousMassage: false,
            savingMassageCheck: false,
            savingComplete: false,
            massageTutorialVisited: false,
            home: createDefaultHome(),
            feedingSummary: {
                feeding_count: 0,
                total_milk_volume_ml: 0
            },
            massageCheck: {
                crying: false,
                sleepNormal: true,
                skinNormal: true,
                abnormalDesc: ''
            },
            completeForm: {
                durationMinutes: ''
            },
            previousMassageForm: {
                operatedDate: '',
                operatedTime: '',
                durationMinutes: ''
            },
            previousDateValue: new Date(),
            previousTimeValue: '20:00',
            pendingPreviousMassageTask: null,
            failedFoodImages: {},
            pendingNativeTask: null,
            completingPendingNativeTask: false
        };
    },
    computed: {
        subject () {
            return this.home.subject || {};
        },
        babyProfile () {
            return this.home.baby_profile || {};
        },
        taskRecord () {
            return this.home.task_record || {};
        },
        dailyTasks () {
            return this.home.daily_tasks || [];
        },
        displayDailyTasks () {
            if (!this.isMilkOnlyAgeStage()) {
                return this.dailyTasks;
            }
            return this.dailyTasks.map(task => {
                if (!this.isFoodTask(task)) {
                    return task;
                }
                const keyMap = {
                    noon_food: 'noon_milk',
                    evening_food: 'evening_milk'
                };
                return {
                    ...task,
                    key: keyMap[task.key] || task.key,
                    type: 'milk',
                    title: '奶',
                    status: task.status === 'completed' ? 'completed' : 'not_started',
                    status_text: task.status === 'completed' ? '已记录' : '待记录',
                    action_text: '编辑',
                    recommendations: [],
                    food_recommendations: []
                };
            });
        },
        showBabySwitcher () {
            return this.babies.length > 1;
        },
        hasStudyAccess () {
            return !!(this.home.has_study_access || this.home.is_enrolled || (this.subject && this.subject.study_no));
        },
        currentBaby () {
            const currentId = String(this.currentBabyId || '');
            return this.babies.find(item => String(item.id) === currentId)
                || this.babies[0]
                || {};
        },
        currentBabyName () {
            return this.currentBaby.baby_name || this.babyProfile.baby_name || '宝宝';
        },
        currentBabyStudyText () {
            const studyNo = this.subject.study_no || this.currentBaby.study_no || this.currentBaby.user_number || '';
            return studyNo ? `研究编号 ${studyNo}` : '切换后将刷新研究记录';
        },
        babyActions () {
            return this.babies.map((baby, index) => {
                const name = baby.baby_name || `宝宝${index + 1}`;
                const studyNo = baby.study_no || baby.user_number || '';
                const isCurrent = String(baby.id) === String(this.currentBabyId);
                const subname = baby.corrected_age_text
                    ? `${baby.corrected_age_text}${studyNo ? ` · ${studyNo}` : ''}`
                    : (studyNo ? `研究编号 ${studyNo}` : '已开通研究');
                return {
                    name: `${isCurrent ? '✓ ' : ''}${name}`,
                    subname,
                    color: isCurrent ? '#2e8b7a' : '#323233',
                    className: isCurrent ? 'baby-action-current' : '',
                    baby
                };
            });
        },
        calendarDays () {
            const base = new Date(`${this.selectedDate}T12:00:00`);
            const today = formatDate(new Date());
            const weekText = ['日', '一', '二', '三', '四', '五', '六'];
            return [-3, -2, -1, 0, 1, 2, 3].map(offset => {
                const date = addDays(base, offset);
                const text = formatDate(date);
                return {
                    date: text,
                    week: weekText[date.getDay()],
                    day: date.getDate(),
                    isToday: text === today,
                    ...this.calendarCompletionMeta(text)
                };
            });
        },
        taskProgressText () {
            const status = this.home.task_status || {};
            const total = normalizeNumber(status.total) || this.displayDailyTasks.length || 0;
            const finished = normalizeNumber(status.completed) || this.displayDailyTasks.filter(task => this.isTaskDone(task)).length;
            return `${finished}/${total} 已完成`;
        },
        calendarTipText () {
            return this.isMilkOnlyAgeStage()
                ? '按当天护理节奏完成奶量记录和小儿推拿任务。'
                : '按当天护理节奏完成奶量记录、小儿推拿和辅食任务。';
        },
        hasMassageAbnormal () {
            return this.massageCheck.crying || !this.massageCheck.sleepNormal || !this.massageCheck.skinNormal;
        },
        selectedDateIsToday () {
            return this.selectedDate === formatDate(new Date());
        },
        previousDateMin () {
            return this.previousDateMax;
        },
        previousDateMax () {
            const selected = new Date(`${this.selectedDate}T12:00:00`);
            selected.setDate(selected.getDate() - 1);
            return selected;
        },
        feedingSummaryText () {
            const summary = this.feedingSummary || {};
            const total = normalizeNumber(summary.total_milk_volume_ml);
            const count = normalizeNumber(summary.feeding_count || summary.feeding_event_count);
            const prefix = this.selectedDateIsToday ? '今日' : '当日';
            return `${prefix}总奶量 ${total ? Math.round(total) : 0}ml · 喂养 ${count} 次`;
        }
    },
    mounted () {
        document.title = '每日照护任务';
        this.token = resolveTcmStudyToken(this.$route);
        this.isNativeShell = this.detectNativeShell();
        this.mockEnabled = this.isMockEnabled();
        this.loadAll();
        window.addEventListener('focus', this.onWindowFocus);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.tcmStudyTaskCompleted = this.onNativeTaskCompleted;
    },
    beforeDestroy () {
        window.removeEventListener('focus', this.onWindowFocus);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        if (window.tcmStudyTaskCompleted === this.onNativeTaskCompleted) {
            delete window.tcmStudyTaskCompleted;
        }
    },
    methods: {
        isMockEnabled () {
            if (process.env.NODE_ENV === 'production') {
                return false;
            }
            const storage = (() => {
                try { return window.localStorage || null; } catch (error) { return null; }
            })();
            return this.$route.query.mock === '1'
                || this.$route.query.mock === 'true'
                || (storage && storage.getItem('tcm_study_mock_enabled') === '1')
                || process.env.VUE_APP_TCM_STUDY_MOCK === 'true';
        },
        detectNativeShell () {
            const query = this.$route.query || {};
            const source = String(query.source || query.platform || query.client || '').toLowerCase();
            const ua = String(window.navigator && window.navigator.userAgent || '');
            return source === 'ios'
                || source === 'app'
                || source === 'native'
                || !!(window.webkit && window.webkit.messageHandlers)
                || /iPhone|iPad|iPod/i.test(ua);
        },
        async loadAll () {
            const isInitialLoad = !this.home || (!this.home.has_study_access && !this.home.is_enrolled && !this.home.subject);
            if (isInitialLoad) {
                this.loading = true;
            }
            this.pageError = '';
            try {
                this.home = normalizeHome(await fetchTcmStudyHome(this.token, this.currentBabyId, this.selectedDate));
                this.babies = Array.isArray(this.home.babies) ? this.home.babies : [];
                this.currentBabyId = this.home.current_baby_id || this.currentBabyId || (this.babies[0] && this.babies[0].id) || '';
                this.syncFeedingSummaryFromHome();
                this.syncTutorialVisited();
            } catch (error) {
                this.pageError = error.message || '加载失败';
            } finally {
                if (isInitialLoad) {
                    this.loading = false;
                }
            }
        },
        syncFeedingSummaryFromHome () {
            const today = this.home.today || {};
            const taskStatus = this.home.task_status || {};
            const taskSummary = this.displayDailyTasks.find(task => this.isFeedingTask(task) && (
                task.feeding_summary || task.care_event_summary || task.total_milk_volume_ml || task.milk_event_count
            )) || {};
            const homeSummary = this.home.feeding_summary
                || today.feeding_summary
                || today.care_event_summary
                || taskSummary.feeding_summary
                || taskSummary.care_event_summary
                || taskSummary
                || taskStatus
                || {};
            this.feedingSummary = {
                feeding_count: normalizeNumber(homeSummary.feeding_count || homeSummary.feeding_event_count || homeSummary.milk_event_count),
                total_milk_volume_ml: normalizeNumber(homeSummary.total_milk_volume_ml)
            };
        },
        async loadFeedingSummary () {
            const requestDate = this.selectedDate;
            try {
                const result = await fetchDailyFeedingStatus(this.token, this.selectedDate, this.subject.id || undefined);
                if (requestDate !== this.selectedDate) {
                    return;
                }
                const summary = (result && (result.feeding_summary || result.care_event_summary)) || {};
                this.feedingSummary = {
                    feeding_count: normalizeNumber(summary.feeding_count || summary.feeding_event_count),
                    total_milk_volume_ml: normalizeNumber(summary.total_milk_volume_ml)
                };
            } catch (error) {
                // 首页主流程不因奶量汇总补充接口失败而中断。
            }
        },
        syncTutorialVisited () {
            let visited = false;
            try {
                visited = window.sessionStorage.getItem(tutorialVisitKey(this.currentBabyId, this.selectedDate)) === '1';
            } catch (error) {
                visited = false;
            }
            this.massageTutorialVisited = visited || ['completed', 'completed_normal', 'completed_abnormal'].indexOf(this.taskRecord.massage_status) !== -1;
        },
        selectDate (date) {
            if (date === this.selectedDate) {
                return;
            }
            this.selectedDate = date;
            this.loadAll();
        },
        onBabySelect (action) {
            const baby = action && action.baby;
            if (!baby || String(baby.id) === String(this.currentBabyId)) {
                this.showBabyPicker = false;
                return;
            }
            this.currentBabyId = baby.id;
            this.showBabyPicker = false;
            this.$toast(`已切换到${baby.baby_name || '宝宝'}`);
            this.loadAll();
        },
        statusColor (task) {
            if (task.status === 'completed') {
                return '#2e8b7a';
            }
            if (task.status === 'aborted') {
                return '#d85b4a';
            }
            if (task.type === 'food') {
                return '#8fcfbf';
            }
            return '#f2b51d';
        },
        calendarCompletionMeta (date) {
            const calendar = this.home.calendar_status || {};
            const meta = calendar[date] || {};
            if (meta.status || meta.text) {
                const normalized = this.normalizeCalendarStatus(meta.status, date);
                return {
                    completionStatus: normalized,
                    completionText: meta.text || this.calendarStatusText(normalized)
                };
            }
            const today = formatDate(new Date());
            if (date > today) {
                return {
                    completionStatus: 'upcoming',
                    completionText: '待开始'
                };
            }
            const total = this.displayDailyTasks.length;
            const finished = this.displayDailyTasks.filter(task => this.isTaskDone(task)).length;
            if (total > 0 && finished >= total) {
                return {
                    completionStatus: 'completed',
                    completionText: '完成'
                };
            }
            if (date < today && this.daysBetween(date, today) <= 3) {
                return {
                    completionStatus: finished > 0 ? 'in-progress' : 'makeup',
                    completionText: finished > 0 ? '进行中' : '可补录'
                };
            }
            if (date === today && finished > 0) {
                return {
                    completionStatus: 'in-progress',
                    completionText: '进行中'
                };
            }
            return {
                completionStatus: date === today ? 'pending' : 'expired',
                completionText: date === today ? '未完成' : '未补录'
            };
        },
        normalizeCalendarStatus (status, date) {
            const raw = String(status || '').replace(/_/g, '-');
            if (['completed', 'in-progress', 'makeup', 'upcoming', 'expired', 'pending'].indexOf(raw) !== -1) {
                return raw;
            }
            if (['complete', 'done', 'finished'].indexOf(raw) !== -1) {
                return 'completed';
            }
            if (['doing', 'ongoing', 'started', 'processing'].indexOf(raw) !== -1) {
                return 'in-progress';
            }
            if (['not-started', 'future'].indexOf(raw) !== -1) {
                return 'upcoming';
            }
            if (['missed', 'overdue'].indexOf(raw) !== -1) {
                return 'expired';
            }
            const today = formatDate(new Date());
            return date > today ? 'upcoming' : 'pending';
        },
        calendarStatusText (status) {
            const textMap = {
                completed: '已完成',
                'in-progress': '进行中',
                makeup: '可补录',
                upcoming: '待开始',
                expired: '未补录',
                pending: '未完成'
            };
            return textMap[status] || '未完成';
        },
        daysBetween (start, end) {
            const startTime = new Date(`${start}T12:00:00`).getTime();
            const endTime = new Date(`${end}T12:00:00`).getTime();
            return Math.round((endTime - startTime) / 86400000);
        },
        shouldSplitAfternoonMixedTask (task) {
            return false;
        },
        isFeedingTask (task) {
            return task && task.type === 'milk';
        },
        isFoodTask (task) {
            return task && task.type === 'food';
        },
        isMilkOnlyAgeStage () {
            const profile = this.babyProfile || {};
            const stage = String(profile.age_stage || '');
            if (stage === 'within_6_months') {
                return true;
            }
            const ageMonth = Number(profile.corrected_age_month);
            if (Number.isFinite(ageMonth)) {
                return ageMonth <= 6;
            }
            const ageText = String(profile.corrected_age_text || '');
            if (!ageText) {
                return false;
            }
            if (ageText.indexOf('周') !== -1) {
                return true;
            }
            const monthMatch = ageText.match(/(\d+(?:\.\d+)?)\s*月/);
            return monthMatch ? Number(monthMatch[1]) <= 6 : false;
        },
        isTaskDone (task) {
            if (!task) {
                return false;
            }
            if (task.type === 'massage') {
                return ['completed', 'completed_normal', 'completed_abnormal', 'unsuitable'].indexOf(task.status) !== -1;
            }
            return task.status === 'completed';
        },
        foodRecommendations (task) {
            const list = (task && (task.recommendations || task.food_recommendations || task.foods)) || [];
            return (Array.isArray(list) ? list : []).slice(0, 2).map(item => {
                const tags = []
                    .concat(item.tags || item.labels || [])
                    .concat([item.reason])
                    .filter(Boolean);
                return {
                    code: item.code || item.id || item.name,
                    id: item.id || item.code || item.name,
                    name: item.name || item.food_name || '辅食推荐',
                    image: normalizeFoodImageUrl(item.image || item.image_url || item.cover_url || item.cover || ''),
                    raw_image: item.image || item.image_url || item.cover_url || item.cover || '',
                    tags: Array.from(new Set(tags)).slice(0, 3),
                    desc: item.desc || item.description || '',
                    description: item.description || item.desc || '',
                    ingredients: item.ingredients || '',
                    steps: item.steps || '',
                    tips: item.tips || '',
                    article_url: item.article_url || '',
                    reason: item.reason || ''
                };
            });
        },
        foodImageFailed (food) {
            return !!(food && this.failedFoodImages[food.image || food.code || food.name]);
        },
        markFoodImageFailed (food) {
            const key = food && (food.image || food.code || food.name);
            if (key) {
                this.$set(this.failedFoodImages, key, true);
            }
        },
        foodTags (task) {
            const tags = task && (task.added_tags || task.food_tags || task.tags);
            return Array.isArray(tags) ? tags.filter(Boolean) : [];
        },
        foodRecordSummary (task) {
            const records = task && (task.food_records || task.food_items || task.added_food_records);
            if (!Array.isArray(records) || !records.length) {
                return '';
            }
            return records.map(item => {
                const name = item.name || item.food_name || item.title || '辅食';
                const weight = item.weight_g || item.amount_g || item.weight || item.amount || '';
                return weight ? `${name} ${weight}g` : name;
            }).join('、');
        },
        taskSubtitle (task) {
            if (!task) {
                return '';
            }
            if (task.summary) {
                return task.summary;
            }
            if (task.type === 'food') {
                return this.isMilkOnlyAgeStage() ? '点击卡片记录或补录奶量' : '根据宝宝接受度少量添加';
            }
            if (task.type === 'mixed') {
                return this.isMilkOnlyAgeStage() ? '点击卡片记录或补录奶量' : '奶量照常记录，辅食按需添加';
            }
            if (task.type === 'milk') {
                return '点击卡片记录或补录奶量';
            }
            return '';
        },
        canContinueMassage (task) {
            return task.type === 'massage' && ['prepared', 'timing', 'paused', 'tutorial_ready', 'completed_pending_feedback'].indexOf(task.status) !== -1;
        },
        showMassagePersistentEntry (task) {
            return task.type === 'massage' && ['completed', 'completed_normal', 'completed_abnormal', 'aborted', 'unsuitable'].indexOf(task.status) !== -1;
        },
        massagePersistentTitle (task) {
            if (task.status === 'unsuitable') {
                return '今天宝宝不适合做';
            }
            const title = task.title || massagePlanTitle(this.home);
            return ['completed', 'completed_normal', 'completed_abnormal'].indexOf(task.status) !== -1 ? `${title}已完成` : `${title}已记录异常`;
        },
        massagePersistentDesc (task) {
            if (task.status === 'unsuitable') {
                return task.unsuitable_reason_desc || this.taskRecord.unsuitable_reason_desc || task.abnormal_desc || '已记录不适合做原因';
            }
            if (['completed', 'completed_normal', 'completed_abnormal'].indexOf(task.status) !== -1) {
                const result = task.post_operation_status_text || this.taskRecord.post_operation_status_text || '操作后待反馈';
                return `操作 ${task.duration_minutes || this.taskRecord.duration_minutes || '-'} 分钟 · ${result}`;
            }
            return task.abnormal_desc || '状态常驻 · 可查看本次记录';
        },
        massagePersistentAction (task) {
            return task.status === 'unsuitable' ? '重新进入' : (this.isMassageCompleted(task) ? '再次操作' : '详情');
        },
        isMassageCompleted (task) {
            return ['completed', 'completed_normal', 'completed_abnormal'].indexOf(task && task.status) !== -1;
        },
        startNewMassage (task) {
            const query = this.massageQuery(task);
            delete query.record_id;
            this.$router.push({
                path: '/tcm-study/massage/prepare',
                query
            });
        },
        massageTaskGuideTitle (task) {
            return `${(task && task.massage_plan_title) || massagePlanTitle(this.home)}操作指引`;
        },
        massageQuery (task = {}) {
            return {
                ...this.$route.query,
                baby_id: this.currentBabyId,
                subject_id: this.subject.id || undefined,
                record_date: this.selectedDate,
                task_key: task.task_key || task.key || 'massage',
                record_id: task.record_id || undefined,
                massage_plan_no: task.massage_plan_no || resolveMassagePlanNo(this.home),
                massage_plan_title: task.massage_plan_title || massagePlanTitle(this.home),
                tutorial_url: massagePlanTutorialUrl(task.massage_plan_no || resolveMassagePlanNo(this.home), task.tutorial_url),
                return_path: '/tcm-study/home'
            };
        },
        scrollToTasks () {
            const target = this.$refs.taskSection;
            if (target && typeof target.scrollIntoView === 'function') {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },
        openMassageCheck (currentTask) {
            const task = currentTask || this.dailyTasks.find(item => item.type === 'massage') || {};
            if (task.locked) {
                this.$dialog.alert({
                    title: `暂未开放${task.title || massagePlanTitle(this.home)}`,
                    message: task.lock_reason || '家长未培训通过，请联系照护师培训',
                    confirmButtonText: '我知道了'
                });
                return;
            }
            if (this.needsPreviousMassageRecord(task)) {
                this.openPreviousMassageDialog(task);
                return;
            }
            this.enterMassagePrepare(task);
        },
        needsPreviousMassageRecord (task) {
            const entry = this.home.massage_entry || {};
            const taskKey = task && (task.task_key || task.key);
            const state = (this.home.previous_massage_states || {})[taskKey] || {};
            if (task && Object.prototype.hasOwnProperty.call(task, 'need_previous_massage_record')) {
                return !!task.need_previous_massage_record;
            }
            if (Object.prototype.hasOwnProperty.call(state, 'need_previous_massage_record')) {
                return !!state.need_previous_massage_record;
            }
            return !!(
                this.home.need_previous_massage_record
                || entry.need_previous_massage_record
                || this.home.unfinished_massage_record
            );
        },
        enterMassagePrepare (task) {
            this.$router.push({
                path: '/tcm-study/massage/prepare',
                query: this.massageQuery(task)
            });
        },
        openPreviousMassageDialog (task) {
            const previousDate = addDays(new Date(`${this.selectedDate}T12:00:00`), -1);
            const taskKey = task.task_key || task.key || 'massage';
            const state = (this.home.previous_massage_states || {})[taskKey] || {};
            const previous = task.unfinished_massage_record
                || task.previous_massage_record
                || state.unfinished_massage_record
                || state.previous_massage_record
                || this.home.unfinished_massage_record
                || this.home.previous_massage_record
                || {};
            this.previousMassageForm = {
                operatedDate: previous.operated_date || previous.record_date || formatDate(previousDate),
                operatedTime: previous.operated_time || (previous.operated_at ? String(previous.operated_at).slice(11, 16) : '20:00'),
                durationMinutes: previous.duration_minutes || ''
            };
            this.previousDateValue = new Date(`${this.previousMassageForm.operatedDate}T12:00:00`);
            this.previousTimeValue = this.previousMassageForm.operatedTime;
            this.pendingPreviousMassageTask = task;
            this.showPreviousMassageDialog = true;
        },
        onPreviousDateConfirm (value) {
            this.previousMassageForm.operatedDate = formatDate(value);
            this.previousDateValue = value;
            this.showPreviousDatePicker = false;
        },
        onPreviousTimeConfirm (value) {
            this.previousMassageForm.operatedTime = value;
            this.previousTimeValue = value;
            this.showPreviousTimePicker = false;
        },
        beforePreviousMassageClose (action, done) {
            if (action !== 'confirm') {
                this.showPreviousMassageDialog = false;
                done();
                return;
            }
            this.submitPreviousMassage(done);
        },
        async submitPreviousMassage (done) {
            const form = this.previousMassageForm;
            const previousDate = this.previousDateMax;
            if (!form.operatedDate || !form.operatedTime) {
                this.$toast('请选择上次推拿时间');
                done(false);
                return;
            }
            if (form.operatedDate !== formatDate(previousDate)) {
                this.$toast('补录日期必须是所选日期的上一自然日');
                done(false);
                return;
            }
            if (!form.durationMinutes || !Number.isInteger(Number(form.durationMinutes)) || Number(form.durationMinutes) <= 0) {
                this.$toast('操作时长必须为正整数');
                done(false);
                return;
            }
            if (this.submittingPreviousMassage) {
                done(false);
                return;
            }
            this.submittingPreviousMassage = true;
            const task = this.pendingPreviousMassageTask || {};
            try {
                await submitPreviousMassageRecord(this.token, {
                    baby_id: this.currentBabyId,
                    subject_id: this.subject.id || undefined,
                    task_key: task.task_key || task.key || 'massage',
                    massage_plan_no: task.massage_plan_no || resolveMassagePlanNo(this.home),
                    record_date: form.operatedDate,
                    current_date: this.selectedDate,
                    operated_at: `${form.operatedDate} ${form.operatedTime}:00`,
                    duration_minutes: Number(form.durationMinutes),
                    source: 'previous_record_prompt'
                });
                done();
                this.showPreviousMassageDialog = false;
                this.enterMassagePrepare(task);
            } catch (error) {
                this.$toast(error.message || '补录失败');
                done(false);
            } finally {
                this.submittingPreviousMassage = false;
            }
        },
        onTaskCardClick (task) {
            if (!task) {
                return;
            }
            if (this.isFeedingTask(task)) {
                this.openNativeFeeding(task);
                return;
            }
            if (this.isFoodTask(task)) {
                const firstFood = this.foodRecommendations(task)[0];
                if (firstFood) {
                    this.openFoodRecipe(firstFood, task);
                } else {
                    this.openNativeFood(task);
                }
                return;
            }
            if (task.type === 'massage') {
                if (task.status === 'not_started') {
                    this.openMassageCheck(task);
                    return;
                }
                if (this.canContinueMassage(task) || this.showMassagePersistentEntry(task)) {
                    this.continueMassage(task);
                }
            }
        },
        continueMassage (task) {
            if (task.status === 'completed_pending_feedback') {
                this.$router.push({
                    path: '/tcm-study/massage/feedback',
                    query: {
                        ...this.massageQuery(task),
                        completed_count: task.completed_count || 1,
                        duration_seconds: task.duration_seconds || ''
                    }
                });
                return;
            }
            if (this.showMassagePersistentEntry(task) && ['completed', 'completed_normal', 'completed_abnormal'].indexOf(task.status) !== -1) {
                this.$router.push({
                    path: '/tcm-study/massage/tutorial',
                    query: {
                        ...this.massageQuery(task),
                        tutorial_url: massagePlanTutorialUrl(task.massage_plan_no || resolveMassagePlanNo(this.home), task.tutorial_url),
                        readonly: '1'
                    }
                });
                return;
            }
            this.$router.push({
                path: '/tcm-study/massage/prepare',
                query: {
                    ...this.massageQuery(task),
                    tutorial_url: massagePlanTutorialUrl(task.massage_plan_no || resolveMassagePlanNo(this.home), task.tutorial_url)
                }
            });
        },
        openLegacyMassageCheck () {
            this.massageCheck = {
                crying: false,
                sleepNormal: true,
                skinNormal: true,
                abnormalDesc: ''
            };
            this.showMassageCheck = true;
        },
        beforeMassageCheckClose (action, done) {
            if (action !== 'confirm') {
                done();
                return;
            }
            this.submitMassageCheck(done);
        },
        async submitMassageCheck (done) {
            if (this.hasMassageAbnormal && !this.massageCheck.abnormalDesc) {
                this.$toast('请填写异常情况');
                done(false);
                return;
            }
            if (this.savingMassageCheck) {
                done(false);
                return;
            }
            this.savingMassageCheck = true;
            try {
                const result = await submitMassageCheck(this.token, {
                    baby_id: this.currentBabyId,
                    subject_id: this.subject.id || undefined,
                    record_date: this.selectedDate,
                    crying: this.massageCheck.crying ? 1 : 0,
                    sleep_normal: this.massageCheck.sleepNormal ? 1 : 0,
                    skin_normal: this.massageCheck.skinNormal ? 1 : 0,
                    abnormal_desc: this.massageCheck.abnormalDesc,
                    task_key: 'massage'
                });
                done();
                await this.loadAll();
                if (result && result.can_open_tutorial) {
                    this.openMassageTutorial();
                } else {
                    this.$toast('存在异常，已终止推拿');
                }
            } catch (error) {
                this.$toast(error.message || '提交失败');
                done(false);
            } finally {
                this.savingMassageCheck = false;
            }
        },
        openMassageTutorial () {
            this.massageTutorialVisited = true;
            try {
                window.sessionStorage.setItem(tutorialVisitKey(this.currentBabyId, this.selectedDate), '1');
            } catch (error) {}
            this.$router.push({
                path: '/tcm-study/massage/tutorial',
                query: {
                    ...this.massageQuery(),
                    task_key: 'massage',
                    tutorial_url: massagePlanTutorialUrl(resolveMassagePlanNo(this.home))
                }
            });
        },
        onWindowFocus () {
            if (this.pendingNativeTask) {
                this.completePendingNativeTask();
                return;
            }
            if (this.massageTutorialVisited) {
                this.loadAll();
            }
        },
        onVisibilityChange () {
            if (document.hidden) {
                return;
            }
            if (this.pendingNativeTask) {
                this.completePendingNativeTask();
                return;
            }
            if (this.massageTutorialVisited) {
                this.loadAll();
            }
        },
        openCompleteDialog () {
            this.completeForm.durationMinutes = this.taskRecord.duration_minutes || '';
            this.showCompleteDialog = true;
        },
        beforeCompleteClose (action, done) {
            if (action !== 'confirm') {
                done();
                return;
            }
            this.submitMassageComplete(done);
        },
        async submitMassageComplete (done) {
            if (!this.completeForm.durationMinutes || Number(this.completeForm.durationMinutes) <= 0) {
                this.$toast('请填写操作时长');
                done(false);
                return;
            }
            if (this.savingComplete) {
                done(false);
                return;
            }
            this.savingComplete = true;
            try {
                await submitMassageComplete(this.token, {
                    baby_id: this.currentBabyId,
                    subject_id: this.subject.id || undefined,
                    record_date: this.selectedDate,
                    task_key: 'massage',
                    duration_minutes: Number(this.completeForm.durationMinutes)
                });
                done();
                this.$toast.success('已完成');
                await this.loadAll();
            } catch (error) {
                this.$toast(error.message || '提交失败');
                done(false);
            } finally {
                this.savingComplete = false;
            }
        },
        async onNativeTaskCompleted (payload = {}) {
            const pending = this.pendingNativeTask || {};
            const taskKey = payload.task_key || pending.task_key;
            if (!taskKey) {
                this.loadAll();
                return;
            }
            try {
                await submitTaskComplete(this.token, {
                    baby_id: payload.baby_id || this.currentBabyId || undefined,
                    subject_id: payload.subject_id || this.subject.id || undefined,
                    record_date: payload.record_date || this.selectedDate,
                    task_key: taskKey,
                    food_tags: payload.food_tags || payload.added_tags || []
                }).then(result => {
                    if (result && Array.isArray(result.daily_tasks)) {
                        this.home = normalizeHome({
                            ...this.home,
                            daily_tasks: result.daily_tasks,
                            task_status: result.task_status || this.home.task_status,
                            calendar_status: result.calendar_status || this.home.calendar_status
                        });
                    }
                    if (result && result.feeding_summary) {
                        this.feedingSummary = {
                            feeding_count: normalizeNumber(result.feeding_summary.feeding_count || result.feeding_summary.feeding_event_count || result.feeding_summary.milk_event_count),
                            total_milk_volume_ml: normalizeNumber(result.feeding_summary.total_milk_volume_ml)
                        };
                    }
                    if (result && result.food_added_tags && Array.isArray(this.home.daily_tasks)) {
                        this.home.daily_tasks = this.home.daily_tasks.map(task => {
                            if (!this.isFoodTask(task)) {
                                return task;
                            }
                            return {
                                ...task,
                                status: result.food_completed ? 'completed' : task.status,
                                status_text: result.food_completed ? '已添加' : task.status_text,
                                added_tags: result.food_added_tags
                            };
                        });
                    }
                });
                this.pendingNativeTask = null;
                await this.loadAll();
            } catch (error) {
                this.$toast(error.message || '任务状态同步失败');
            }
        },
        async completePendingNativeTask () {
            const pending = this.pendingNativeTask;
            if (!pending || this.completingPendingNativeTask) {
                return;
            }
            if (Date.now() - pending.opened_at < 800) {
                return;
            }
            this.completingPendingNativeTask = true;
            try {
                await this.onNativeTaskCompleted(pending);
            } finally {
                this.completingPendingNativeTask = false;
            }
        },
        openNativeFeeding (task) {
            const payload = {
                source: 'tcm-study',
                record_date: this.selectedDate,
                date: this.selectedDate,
                baby_id: this.currentBabyId || undefined,
                subject_id: this.subject.id || undefined,
                task_key: task.key,
                allow_makeup: true,
                is_makeup: this.selectedDateIsToday ? 0 : 1
            };
            const pending = {
                type: 'milk',
                task_key: task.key,
                record_date: this.selectedDate,
                baby_id: this.currentBabyId || undefined,
                subject_id: this.subject.id || undefined,
                opened_at: Date.now()
            };
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.openFeedingRecord) {
                this.pendingNativeTask = pending;
                window.webkit.messageHandlers.openFeedingRecord.postMessage(payload);
                return;
            }
            if (window.healthApp && typeof window.healthApp.openFeedingRecord === 'function') {
                this.pendingNativeTask = pending;
                window.healthApp.openFeedingRecord(payload);
                return;
            }
            this.$toast('请返回 App 使用现有喂养记录入口');
        },
        openFoodRecipe (food, task) {
            const encoded = encodeURIComponent(JSON.stringify({
                ...food,
                task_key: task && task.key,
                task_title: task && task.title
            }));
            this.$router.push({
                path: '/tcm-study/food/recipe',
                query: {
                    ...this.$route.query,
                    baby_id: this.currentBabyId || undefined,
                    subject_id: this.subject.id || undefined,
                    record_date: this.selectedDate,
                    food: encoded,
                    return_path: '/tcm-study/home'
                }
            });
        },
        openNativeFood (task) {
            const payload = {
                source: 'tcm-study',
                target: 'complementary-food-record',
                native_route: 'record.baby.food',
                tab: 'record',
                module: 'baby_record_food',
                record_date: this.selectedDate,
                date: this.selectedDate,
                baby_id: this.currentBabyId || undefined,
                subject_id: this.subject.id || undefined,
                task_key: task.key,
                task_title: task.title,
                recommendations: this.foodRecommendations(task),
                allow_makeup: true,
                is_makeup: this.selectedDateIsToday ? 0 : 1
            };
            const pending = {
                type: 'food',
                task_key: task.key,
                record_date: this.selectedDate,
                baby_id: this.currentBabyId || undefined,
                subject_id: this.subject.id || undefined,
                food_tags: this.foodRecommendations(task).map(item => item.name).filter(Boolean),
                opened_at: Date.now()
            };
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.openComplementaryFoodRecord) {
                this.pendingNativeTask = pending;
                window.webkit.messageHandlers.openComplementaryFoodRecord.postMessage(payload);
                return;
            }
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.openFoodRecord) {
                this.pendingNativeTask = pending;
                window.webkit.messageHandlers.openFoodRecord.postMessage(payload);
                return;
            }
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.openBabyRecord) {
                this.pendingNativeTask = pending;
                window.webkit.messageHandlers.openBabyRecord.postMessage(payload);
                return;
            }
            if (window.healthApp && typeof window.healthApp.openComplementaryFoodRecord === 'function') {
                this.pendingNativeTask = pending;
                window.healthApp.openComplementaryFoodRecord(payload);
                return;
            }
            if (window.healthApp && typeof window.healthApp.openFoodRecord === 'function') {
                this.pendingNativeTask = pending;
                window.healthApp.openFoodRecord(payload);
                return;
            }
            if (window.healthApp && typeof window.healthApp.openBabyRecord === 'function') {
                this.pendingNativeTask = pending;
                window.healthApp.openBabyRecord(payload);
                return;
            }
            this.$toast('请返回 App 使用现有辅食记录入口');
        }
    }
};
</script>

<style lang="sass" scoped>
.tcm-study-page
    --page-side-gap: 16px
    --page-top-gap: 12px
    --page-bottom-gap: 28px
    min-height: 100vh
    background: #ffffff
    color: #1d1d1f
    font-size: 14px
    box-sizing: border-box

.tcm-study-page.is-native
    --page-top-gap: max(8px, env(safe-area-inset-top))
    --page-bottom-gap: 34px
    padding-top: 0
    padding-bottom: env(safe-area-inset-bottom)

.page-loading
    display: flex
    justify-content: center
    padding-top: 80px

.empty-panel
    min-height: 100vh
    display: flex
    flex-direction: column
    align-items: center
    padding: 96px 24px 24px
    box-sizing: border-box

.error-panel
    padding: 40px 16px 24px

.content
    padding: var(--page-top-gap) 0 calc(var(--page-bottom-gap) + env(safe-area-inset-bottom))

.page-heading
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    padding: 2px var(--page-side-gap) 12px

.page-heading span
    flex: none
    color: #8a8f94
    font-size: 13px
    line-height: 20px

.daily-entry-card
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    margin: 0 var(--page-side-gap) 14px
    padding: 16px
    border-radius: 8px
    background: linear-gradient(135deg, #eaf7f2 0%, #fff8e8 100%)
    border: 1px solid #d6eee6
    box-shadow: 0 8px 24px rgba(46, 139, 122, .12)

.daily-entry-main
    min-width: 0
    display: flex
    flex-direction: column
    gap: 3px

.daily-entry-main span
    color: #2e8b7a
    font-size: 13px
    line-height: 18px
    font-weight: 700

.daily-entry-main strong
    color: #17211f
    font-size: 24px
    line-height: 30px
    font-weight: 800

.daily-entry-main em
    color: #66706d
    font-style: normal
    font-size: 12px
    line-height: 17px

.daily-entry-action
    flex: none
    width: 72px
    height: 72px
    border-radius: 8px
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 6px
    background: #2e8b7a
    color: #ffffff

.daily-entry-action .van-icon
    font-size: 24px

.daily-entry-action b
    font-size: 13px
    line-height: 17px
    font-weight: 700

.profile-strip
    display: flex
    align-items: center
    justify-content: space-between
    gap: 10px
    padding: 0 var(--page-side-gap) 12px
    color: #777777
    font-size: 13px
    line-height: 18px

.profile-strip > div
    min-width: 0
    display: flex
    flex-direction: column
    gap: 2px

.profile-strip span
    color: #2e8b7a
    font-weight: 600

.profile-strip em
    min-width: 0
    font-style: normal
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

.profile-strip button
    flex: none
    display: inline-flex
    align-items: center
    gap: 6px
    min-height: 38px
    padding: 0 14px
    border: 1px solid #7ac8b1
    border-radius: 19px
    background: #dff4ee
    color: #2e8b7a
    font-size: 14px
    font-weight: 700
    box-shadow: 0 4px 12px rgba(46, 139, 122, .16)

.calendar-section h2,
.timeline-section h2
    margin: 0
    color: #111111
    font-size: 18px
    line-height: 24px
    font-weight: 700

.calendar-section
    padding: 0 var(--page-side-gap) 18px

.date-strip
    display: grid
    grid-template-columns: repeat(7, 1fr)
    gap: 4px
    margin-top: 14px

.date-item
    min-width: 0
    min-height: 80px
    padding: 0
    border: 0
    background: transparent
    color: #777777
    display: flex
    flex-direction: column
    align-items: center
    justify-content: flex-start
    gap: 6px

.date-item span
    font-size: 12px
    line-height: 16px

.date-item strong
    width: 36px
    height: 36px
    display: flex
    align-items: center
    justify-content: center
    border-radius: 50%
    color: #777777
    font-size: 15px
    font-weight: 500

.date-item em
    height: 12px
    color: #777777
    font-style: normal
    font-size: 12px
    line-height: 12px

.date-item i
    min-height: 16px
    padding: 0 4px
    border-radius: 8px
    background: #f1f3f5
    color: #8a8f94
    font-style: normal
    font-size: 10px
    line-height: 16px
    white-space: nowrap

.date-item.is-active strong
    background: #d68a00
    color: #ffffff
    font-weight: 700

.date-item.is-active em
    color: #9a6400
    font-weight: 600

.date-item.is-completed i
    background: #eaf7f2
    color: #2e8b7a

.date-item.is-completed strong
    color: #2e8b7a

.date-item.is-in-progress i
    background: #e8f3ff
    color: #1d6fd6

.date-item.is-in-progress strong
    color: #1d6fd6

.date-item.is-makeup i
    background: #fff4dc
    color: #9a6400

.date-item.is-makeup strong
    border: 1px solid #f2c46d
    color: #9a6400

.date-item.is-pending i
    background: #fff4dc
    color: #9a6400

.date-item.is-upcoming i
    background: #f1f3f5
    color: #8a8f94

.date-item.is-upcoming strong
    color: #a6abb0

.date-item.is-expired i
    background: #fff1ef
    color: #d85b4a

.date-item.is-unknown i
    background: #f1f3f5
    color: #8a8f94

.calendar-tip
    margin: 8px 0 0
    color: #777777
    font-size: 13px
    line-height: 20px

.timeline-section
    border-top: 8px solid #f5f5f5
    padding: 18px var(--page-side-gap) 0

.section-title-row
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    margin-bottom: 14px

.section-title-row span
    color: #9b9b9b
    font-size: 13px

.task-timeline
    display: flex
    flex-direction: column

.timeline-row
    display: grid
    grid-template-columns: 54px 22px minmax(0, 1fr)
    align-items: stretch
    min-height: 86px

.time-label
    padding-top: 13px
    color: #111111
    font-size: 14px
    line-height: 19px
    font-weight: 600
    text-align: right

.line-cell
    position: relative
    display: flex
    justify-content: center

.line-cell::before
    content: ''
    position: absolute
    top: 0
    bottom: 0
    width: 1px
    background: #7cdac4

.timeline-row:last-child .line-cell::before
    bottom: auto
    height: 30px

.line-cell span
    position: relative
    z-index: 1
    width: 8px
    height: 8px
    margin-top: 24px
    border: 2px solid #58d5bb
    border-radius: 50%
    background: #ffffff

.task-card
    display: block
    width: 100%
    margin: 0 0 12px
    padding: 11px 12px
    background: #ffffff
    border: 1px solid #f0f0f0
    border-radius: 8px
    box-sizing: border-box
    box-shadow: 0 2px 10px rgba(31, 41, 55, .04)
    text-align: left
    outline: none

.task-card.is-clickable
    cursor: pointer

.task-card.is-clickable:active
    transform: translateY(1px)
    background: #fbfbfb

.task-card.is-massage
    background: #fff9ec

.task-card.status-completed
    background: #f1faf6

.task-card.status-aborted
    background: #fff3f1

.task-main
    display: flex
    align-items: flex-start
    justify-content: space-between
    gap: 8px

.task-main > div
    min-width: 0

.task-main h3
    margin: 0
    color: #2f2f2f
    font-size: 15px
    line-height: 21px
    font-weight: 600

.task-main p
    margin: 4px 0 0
    color: #777777
    font-size: 12px
    line-height: 17px

.task-main ::v-deep .van-tag
    flex: none
    max-width: 86px
    padding: 0 6px
    font-size: 11px
    line-height: 18px
    white-space: normal

.feeding-summary
    display: flex
    align-items: center
    justify-content: space-between
    gap: 8px
    margin-top: 10px
    padding: 8px 10px
    border-radius: 8px
    background: #f7faf9
    color: #4e5969
    font-size: 12px
    line-height: 17px

.feeding-summary span
    min-width: 0
    flex: 1

.feeding-summary em
    flex: none
    min-height: 24px
    display: inline-flex
    align-items: center
    padding: 0 10px
    border-radius: 12px
    background: #2e8b7a
    color: #ffffff
    font-style: normal
    font-size: 12px
    line-height: 16px

.food-recommend-card
    display: grid
    grid-template-columns: minmax(0, 1fr)
    gap: 8px
    margin-top: 10px
    padding: 0
    background: transparent
    border: 0

.food-recommend-title
    display: flex
    align-items: center
    gap: 8px
    margin-top: 12px
    color: #8a6a35
    font-size: 12px
    line-height: 17px
    font-weight: 600

.food-recommend-title span
    height: 1px
    flex: 1
    background: #f0dfbf

.food-recommend-item
    min-width: 0
    display: flex
    align-items: flex-start
    gap: 8px
    min-height: 64px
    padding: 10px
    border-radius: 8px
    background: #fffaf1
    border: 1px solid #f5e2bd
    cursor: pointer

.food-image
    flex: none
    width: 44px
    height: 44px
    border-radius: 8px
    overflow: hidden
    background: linear-gradient(135deg, #eaf7f2 0%, #fff3d8 100%)
    display: flex
    align-items: center
    justify-content: center
    color: #2e8b7a
    font-size: 20px
    font-weight: 700

.food-info
    min-width: 0
    flex: 1
    padding-top: 1px

.food-image img
    width: 100%
    height: 100%
    display: block
    object-fit: cover

.food-recommend-item b
    display: block
    color: #263331
    font-size: 13px
    line-height: 17px
    font-weight: 700
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

.food-recommend-tags
    display: flex
    flex-wrap: wrap
    gap: 4px
    margin-top: 5px

.food-recommend-tags .van-tag
    max-width: 100%
    font-size: 10px
    line-height: 14px
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

.food-tags
    display: flex
    flex-wrap: wrap
    gap: 8px
    margin-top: 10px

.food-record-summary
    margin-top: 10px
    padding: 8px 10px
    border-radius: 8px
    background: #f3fbf8
    color: #2e6258
    font-size: 12px
    line-height: 17px

.food-add-row
    display: flex
    align-items: center
    justify-content: space-between
    gap: 8px
    margin-top: 10px

.food-add-row button
    min-width: 72px
    min-height: 30px
    border: 0
    border-radius: 8px
    background: #2e8b7a
    color: #ffffff
    font-size: 13px
    line-height: 18px

.task-actions
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 8px
    margin-top: 10px

.task-actions button
    min-height: 32px
    padding: 0 12px
    border: 0
    border-radius: 8px
    background: #ffffff
    color: #333333
    font-size: 13px
    line-height: 18px

.task-actions .tutorial-card
    position: relative
    width: 100%
    min-height: 76px
    display: flex
    align-items: center
    justify-content: space-between
    gap: 10px
    padding: 12px
    border-radius: 8px
    background: #fff4dc
    border: 1px solid #f6d8a6
    color: #6f3a00
    text-align: left

.tutorial-card span
    display: flex
    flex-direction: column
    gap: 3px
    min-width: 0

.tutorial-card b
    font-size: 15px
    line-height: 21px
    font-weight: 600

.tutorial-card small
    color: #8c6a3c
    font-size: 12px
    line-height: 17px

.tutorial-card em
    flex: none
    min-height: 26px
    display: inline-flex
    align-items: center
    padding: 0 10px
    border-radius: 14px
    background: #d68a00
    color: #ffffff
    font-style: normal
    font-size: 12px
    line-height: 16px

.tutorial-card .lock-icon
    position: absolute
    right: 10px
    top: 10px
    color: #8a3b00
    font-size: 16px

.tutorial-card.is-locked
    background: #f4f5f7
    color: #4e5969
    border: 1px solid #e5e6eb

.tutorial-card.is-locked small
    color: #86909c

.tutorial-card.is-locked em
    background: #e5e6eb
    color: #4e5969

.tutorial-card.is-locked .lock-icon
    color: #86909c

.tutorial-card.is-persistent
    background: #f1faf6
    border-color: #c9e8de
    color: #1f6f60

.tutorial-card.is-persistent small
    color: #4f8f82

.tutorial-card.is-persistent em
    background: #2e8b7a

.task-actions button.primary
    background: #d68a00
    color: #ffffff

.done-text,
.aborted-text
    color: #777777
    font-size: 13px
    line-height: 20px

.aborted-text
    color: #d85b4a

.check-dialog,
.complete-dialog
    padding: 8px 0

::v-deep .van-dialog__content .van-cell
    padding-left: 18px
    padding-right: 18px
</style>
